import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Eye, Download, Filter, Search, Camera, Trash2, Plus } from 'lucide-react';
import ImageModalViewer from './ImageModalViewer';
import ImageFilters from './ImageFilters';
import ImageDownload from './ImageDownload';

const HSEAssessmentImageGallery = ({ 
  assessmentId, 
  images = [], 
  categorizedImages = {},
  onImageUpload,
  onImageDelete,
  canEdit = false,
  className = ''
}) => {
  const [expandedCategories, setExpandedCategories] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Image categories aligned to backend keys
  const imageCategories = {
    legal_wall_charts: 'Legal/Compliance',
    fire_fighting_equipment: 'Fire Fighting Equipment',
    automatic_sprinkler_system: 'Automatic Sprinkler System',
    emergency_preparedness: 'Emergency Preparedness',
    evacuation_routes_exits: 'Evacuation Routes/Exits',
    fire_detection_alarm: 'Fire Detection/Alarm',
    signage_displayed: 'Signage Displayed',
    electrical_safety: 'Electrical Safety',
    hazardous_materials: 'Hazardous Materials',
    general_conditions: 'General Conditions',
    stacking_articles: 'Stacking Articles',
    overview: 'Overview',
    other: 'Other'
  };

  // Legacy alias mapping -> standard backend keys
  const categoryAliases = {
    legal_compliance: 'legal_wall_charts',
    sprinkler_systems: 'automatic_sprinkler_system',
    evacuation_routes: 'evacuation_routes_exits',
    fire_detection_systems: 'fire_detection_alarm',
    signage: 'signage_displayed'
  };

  // Initialize expanded state for all categories
  useEffect(() => {
    const initialExpanded = {};
    Object.keys(imageCategories).forEach(category => {
      initialExpanded[category] = true;
    });
    setExpandedCategories(initialExpanded);
  }, []);

  // Process images - convert flat array to categorized if needed
  const processImages = () => {
    console.log('[HSE Gallery] received categorizedImages:', categorizedImages);
    console.log('[HSE Gallery] received images:', images);

    if (categorizedImages && typeof categorizedImages === 'object') {
      // Support model-shaped object { images: {category: [...]} } and raw dict {category: [...]}.
      const source = (categorizedImages.images && typeof categorizedImages.images === 'object')
        ? categorizedImages.images
        : categorizedImages;
      console.log('[HSE Gallery] derived source keys:', source ? Object.keys(source) : null);
      if (source && Object.keys(source).length > 0) {
        // Normalize entries to expected UI fields and remap aliases
        const normalized = {};
        Object.entries(source).forEach(([catKey, imgs]) => {
          const stdCatKey = categoryAliases[catKey] || catKey;
          normalized[stdCatKey] = (imgs || []).map((img, idx) => {
            const base64 = (img && (img.data || img.url)) || img;
            return {
              id: (img && img.id) || `img_${stdCatKey}_${idx}`,
              data: typeof base64 === 'string' ? base64 : base64,
              category: (img && img.category) || stdCatKey,
              field_name: (img && img.field_name) || '',
              description: (img && img.description) || '',
              upload_timestamp: (img && (img.upload_timestamp || img.uploaded_at)) || new Date().toISOString(),
              uploader: (img && (img.uploader || img.uploaded_by)) || 'Unknown',
              file_name: (img && (img.file_name || img.filename)) || `image_${idx + 1}.jpg`,
              size: (img && (img.size || img.file_size)) || 0,
              mime_type: (img && (img.mime_type || img.content_type)) || 'image/jpeg'
            };
          });
        });
        console.log('[HSE Gallery] normalized categories:', Object.keys(normalized), 'counts:', Object.fromEntries(Object.entries(normalized).map(([k,v]) => [k, v.length])));
        return normalized;
      }
    }

    // Convert flat array to categorized structure
    const categorized = {};
    images.forEach((image, index) => {
      const rawCategory = (image && image.category) || 'other';
      const category = categoryAliases[rawCategory] || rawCategory;
      if (!categorized[category]) {
        categorized[category] = [];
      }
      const dataVal = typeof image === 'string' ? image : ((image && (image.data || image.url)) || image);
      const imgObj = {
        id: (image && image.id) || `img_${index}`,
        data: dataVal,
        category: category,
        field_name: (image && image.field_name) || `Image ${index + 1}`,
        description: (image && image.description) || '',
        upload_timestamp: (image && (image.upload_timestamp || image.uploaded_at)) || new Date().toISOString(),
        uploader: (image && (image.uploader || image.uploaded_by)) || 'Unknown',
        file_name: (image && (image.file_name || image.filename)) || `image_${index + 1}.jpg`,
        size: (image && (image.size || image.file_size)) || 0,
        mime_type: (image && (image.mime_type || image.content_type)) || 'image/jpeg'
      };
      categorized[category].push(imgObj);
    });
    console.log('[HSE Gallery] built categorized from flat images:', Object.keys(categorized), 'counts:', Object.fromEntries(Object.entries(categorized).map(([k,v]) => [k, v.length])));
    return categorized;
  };

  const processedImages = processImages();
  console.log('[HSE Gallery] processedImages keys:', Object.keys(processedImages || {}));
  console.log('[HSE Gallery] total images after process:', Object.values(processedImages || {}).reduce((t, arr) => t + (Array.isArray(arr) ? arr.length : 0), 0));
  console.log('[HSE Gallery] sample processed image:', (() => { const entry = Object.entries(processedImages || {}).find(([, v]) => Array.isArray(v) && v.length > 0); return entry ? { category: entry[0], first: entry[1][0] } : null; })());

  // Ensure all categories are default-expanded, including backend-provided keys
  useEffect(() => {
    const keys = Object.keys(processedImages || {});
    if (keys.length > 0) {
      setExpandedCategories(prev => {
        const next = { ...prev };
        keys.forEach(k => {
          if (typeof next[k] === 'undefined') {
            next[k] = true;
          }
        });
        return next;
      });
    }
  }, [JSON.stringify(processedImages)]);

  // Filter images based on category and search term
  const getFilteredImages = () => {
    let filtered = { ...processedImages };

    // Filter by category
    if (filterCategory !== 'all') {
      filtered = { [filterCategory]: filtered[filterCategory] || [] };
    }

    // Filter by search term
    if (searchTerm) {
      Object.keys(filtered).forEach(category => {
        filtered[category] = (filtered[category] || []).filter(image => 
          ((image.field_name || '').toLowerCase().includes(searchTerm.toLowerCase())) ||
          ((image.description || '').toLowerCase().includes(searchTerm.toLowerCase())) ||
          ((image.file_name || '').toLowerCase().includes(searchTerm.toLowerCase()))
        );
      });
    }

    console.log('[HSE Gallery] filterCategory:', filterCategory, 'searchTerm:', searchTerm);
    console.log('[HSE Gallery] filtered keys:', Object.keys(filtered), 'counts:', Object.fromEntries(Object.entries(filtered).map(([k,v]) => [k, (Array.isArray(v) ? v.length : 0)])));

    return filtered;
  };

  const filteredImages = getFilteredImages();

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Handle image click for full-screen view
  const handleImageClick = (image) => {
    setSelectedImage(image);
  };

  // Handle image delete
  const handleImageDelete = async (imageId, category) => {
    if (!canEdit) return;
    
    if (window.confirm('Are you sure you want to delete this image?')) {
      setIsLoading(true);
      try {
        await onImageDelete(imageId, category);
      } catch (error) {
        console.error('Error deleting image:', error);
        alert('Failed to delete image. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Get total image count
  const getTotalImageCount = () => {
    return Object.values(filteredImages).reduce((total, categoryImages) => total + categoryImages.length, 0);
  };

  // Helper to compute and log image src
  const deriveImageSrc = (image) => {
    const raw = image?.data;
    let src = '';
    if (typeof raw === 'string') {
      src = raw.startsWith('data:') ? raw : `data:${image?.mime_type || 'image/jpeg'};base64,${raw}`;
    }
    console.log('[HSE Gallery] img src computed:', image?.id, !!src, src ? src.slice(0, 32) + '...' : null);
    return src;
  };

  // Render category section
  const renderCategorySection = (category, categoryImages) => {
    const isExpanded = expandedCategories[category];
    const categoryName = imageCategories[category] || category;

    return (
      <div key={category} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div 
          className="flex items-center justify-between p-4 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
          onClick={() => toggleCategory(category)}
        >
          <div className="flex items-center space-x-3">
            <Camera className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">{categoryName}</h3>
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
              {categoryImages.length} images
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          )}
        </div>

        {isExpanded && (
          <div className="p-4">
            {categoryImages.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Camera className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>No images in this category</p>
                {canEdit && (
                  <button
                    onClick={() => onImageUpload && onImageUpload(category)}
                    className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center mx-auto"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Upload Image
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {categoryImages.map((image) => (
                  <div
                    key={image.id}
                    className="group relative bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-200 cursor-pointer"
                  >
                    <div 
                      className="aspect-square bg-gray-100 overflow-hidden"
                      onClick={() => handleImageClick(image)}
                    >
                      <img
                        src={deriveImageSrc(image)}
                        alt={image.field_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                        loading="lazy"
                      />
                    </div>
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 pointer-events-none" />
                    
                    {/* Action buttons */}
                    <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleImageClick(image);
                        }}
                        className="bg-white bg-opacity-90 hover:bg-opacity-100 rounded-full p-1 shadow-md transition-all duration-200"
                        title="View full size"
                      >
                        <Eye className="w-4 h-4 text-gray-700" />
                      </button>
                      {canEdit && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleImageDelete(image.id, category);
                          }}
                          className="bg-red-500 bg-opacity-90 hover:bg-opacity-100 rounded-full p-1 shadow-md transition-all duration-200"
                          title="Delete image"
                          disabled={isLoading}
                        >
                          <Trash2 className="w-4 h-4 text-white" />
                        </button>
                      )}
                    </div>

                    {/* Image info */}
                    <div className="p-2 bg-white">
                      <p className="text-xs font-medium text-gray-900 truncate" title={image.field_name}>
                        {image.field_name}
                      </p>
                      {image.description && (
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2" title={image.description}>
                          {image.description}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(image.upload_timestamp).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header with filters and actions */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-3">
            <Camera className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">Assessment Images</h2>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
              {getTotalImageCount()} images
            </span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search images..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Category Filter */}
            <ImageFilters
              categories={imageCategories}
              selectedCategory={filterCategory}
              onCategoryChange={setFilterCategory}
            />

            {/* Download All */}
            {getTotalImageCount() > 0 && (
              <ImageDownload
                assessmentId={assessmentId}
                images={filteredImages}
                categoryFilter={filterCategory}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              />
            )}
          </div>
        </div>
      </div>

      {/* Category Sections */}
      <div className="space-y-4">
        {Object.keys(filteredImages).length === 0 ? (
          <div className="text-center py-12 bg-white border border-gray-200 rounded-lg">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No images found</h3>
            <p className="text-gray-500 mb-4">
              {searchTerm || filterCategory !== 'all' 
                ? 'Try adjusting your search or filter criteria.'
                : 'No images have been uploaded for this assessment yet.'
              }
            </p>
            {canEdit && onImageUpload && (
              <button
                onClick={() => onImageUpload('other')}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center mx-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Upload First Image
              </button>
            )}
          </div>
        ) : (
          Object.entries(filteredImages).map(([category, categoryImages]) =>
            renderCategorySection(category, categoryImages)
          )
        )}
      </div>

      {/* Image Modal Viewer */}
      {selectedImage && (
        <ImageModalViewer
          image={selectedImage}
          onClose={() => setSelectedImage(null)}
          onDelete={canEdit ? handleImageDelete : null}
        />
      )}
    </div>
  );
};

export default HSEAssessmentImageGallery;